import * as React from 'react';

import {Card} from 'shells/card/card';
import {FeatureStatusCard} from 'shells/card/feature-status-card';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import type {SubdomainOption} from 'shells/subdomain-select/subdomain-select';
import {Body} from 'shells/typography/body';

import type {DomainFeatureStatusKey} from 'nutshell-graphql-types';
import type {
    GetDomainFeatureStatuses_domainFeatureStatuses as DomainFeatureStatus,
    GetSubdomains_subdomains as Subdomain,
} from 'nutshell-graphql-types';

import {useGetAnalyticsStatus} from '../../email-marketing/analytics/use-get-analytics-status';
import {RouteContext} from '../../route-context';

import {
    getFeatureStatusBadge,
    getFeatureStatusTitle,
    getFeatureStatusSubtitle,
    getFeatureStatusIcon,
    getFeatureStatusTertiaryData,
    getFeatureStatusBanner,
} from './feature-status-constants';
import {
    useSetClickTrackingDomain,
    useSetFormsSchedulerDomain,
    useSetLandingPageDomain,
    useSetBootSnippetDomain,
} from './graphql/hooks';
import {createSubdomainOptions} from './helpers';

type Props = {
    subdomains: Subdomain[];
    domainFeatureStatuses: DomainFeatureStatus[];
    isLoading: boolean;
    onAddDomain: () => void;
    onFeatureUpdate: () => Promise<any>;
};

export function FeatureStatuses(props: Props) {
    const {analyticsStatus, refetch: refetchAnalyticsStatus} = useGetAnalyticsStatus();
    const {setClickTrackingDomain} = useSetClickTrackingDomain();
    const {setFormsSchedulerDomain} = useSetFormsSchedulerDomain();
    const {setLandingPageDomain} = useSetLandingPageDomain();
    const {setBootSnippetDomain} = useSetBootSnippetDomain();

    const {isEmbedded} = React.useContext(RouteContext);

    const fetchedSubdomains = props.subdomains ? createSubdomainOptions(props.subdomains) : [];
    const hasNewSnippet = analyticsStatus && analyticsStatus.status === 'CONFIGURED';

    const handleSelectSubdomain = async (
        key: DomainFeatureStatusKey,
        option?: SubdomainOption | null
    ) => {
        switch (key) {
            case 'customClickTracking':
                return setClickTrackingDomain({
                    domainId: option ? option.value : null,
                });
            case 'landingPages':
                return setLandingPageDomain({
                    domainId: option ? option.value : null,
                });
            case 'schedulerAndForms':
                return setFormsSchedulerDomain({
                    domainId: option ? option.value : null,
                });
            case 'bootSnippet':
                return setBootSnippetDomain({
                    domainId: option ? option.value : null,
                }).then(() => {
                    refetchAnalyticsStatus();
                });
        }
    };

    return (
        <Card hasBorder={true} overflowVisible={true}>
            <div className='pad-24'>
                <StackLegacy spacing={16}>
                    <Body size='large' isBold={true} color='dark'>
                        How DNS records make Nutshell work better
                    </Body>
                    <div className='flex flex-dir-col gap-8'>
                        {props.isLoading ? (
                            <LoadingPage />
                        ) : (
                            <>
                                {props.domainFeatureStatuses.map((domainFeatureStatus) => {
                                    const selectedSubdomain =
                                        fetchedSubdomains.find((subdomain) => {
                                            return (
                                                subdomain.value ===
                                                (domainFeatureStatus.fromDomain &&
                                                    domainFeatureStatus.fromDomain.id)
                                            );
                                        }) || null; // We need to default to null to clear values appropriately

                                    const handleSelectSubdomainForFeature = (
                                        option?: SubdomainOption | null
                                    ) => {
                                        handleSelectSubdomain(domainFeatureStatus.key, option).then(
                                            () => {
                                                props.onFeatureUpdate();
                                            }
                                        );
                                    };

                                    // Don't render the snippet features card for those using the embedded version of Nutshell
                                    if (domainFeatureStatus.key === 'bootSnippet' && isEmbedded) {
                                        return null;
                                    }

                                    return (
                                        <FeatureStatusCard
                                            key={domainFeatureStatus.key}
                                            title={getFeatureStatusTitle(domainFeatureStatus.key)}
                                            subtitle={getFeatureStatusSubtitle(
                                                domainFeatureStatus.key
                                            )}
                                            tertiaryData={getFeatureStatusTertiaryData(
                                                domainFeatureStatus.key,
                                                fetchedSubdomains,
                                                selectedSubdomain,
                                                handleSelectSubdomainForFeature,
                                                props.onAddDomain
                                            )}
                                            statusBadge={getFeatureStatusBadge(
                                                domainFeatureStatus.key,
                                                fetchedSubdomains,
                                                domainFeatureStatus.status
                                            )}
                                            overflowVisible={true}
                                            icon={getFeatureStatusIcon(domainFeatureStatus.key)}
                                            banner={getFeatureStatusBanner(
                                                domainFeatureStatus.key,
                                                fetchedSubdomains,
                                                selectedSubdomain,
                                                hasNewSnippet
                                            )}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </div>
                </StackLegacy>
            </div>
        </Card>
    );
}
